import { Graph, Link } from './diagram.ts'

export type Color = {
    r: number,
    g: number,
    b: number
}

export function hexToRgb(hex: string) {
    const hexCode = hex.replace('#', '')
    const bigint = parseInt(hexCode, 16)

    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255
    }
}

function componentToHex(c: number) {
    const hex = Math.floor(c).toString(16)
    return hex.length === 1 ? "0" + hex : hex
}

export function rgbToHex(c: Color) {
    return "#" + componentToHex(c.r) + componentToHex(c.g) + componentToHex(c.b)
}

export function meanColor(colors: Color[]) {
    return colors.reduce((partialSum: Color, color) => ({
        r: partialSum.r + color.r / colors.length,
        g: partialSum.g + color.g / colors.length,
        b: partialSum.b + color.b / colors.length,
    }), { r: 0, g: 0, b: 0 })
}

export function weightedMeanColor(c1: Color, w1: number, c2: Color, w2: number) {
    return {
        r: c1.r * w1 + c2.r * w2,
        g: c1.g * w1 + c2.g * w2,
        b: c1.b * w1 + c2.b * w2
    }
}

export function isValidColor(color?: Color) {
    if (!color) {
        return false
    }

    return color.r >= 0 && color.b >= 0 && color.g >= 0 &&
        color.r <= 255 && color.b <= 255 && color.g <= 255
}

export function computeLinkColors(graph: Graph) {
    const nodeQueue = graph.nodes.filter((node) => node.sourceLinks.length === 0)

    const visited = new Set<Link>()
    while (nodeQueue.length > 0) {
        const node = nodeQueue.pop()!

        if (node.sourceLinks!.length === 0) {
            for (const link of node.targetLinks!) {
                if (!visited.has(link)) {
                    link.color = { ...node.color }
                    nodeQueue.unshift(link.target)
                    visited.add(link)
                }
            }

            continue
        }

        const sourceLinksMeanColor =
            meanColor(node.sourceLinks!.filter((link) => link.color).map((link) => link.color!))

        const weightedColor = weightedMeanColor(
            sourceLinksMeanColor,
            node.outgoingValue < node.incomingValue ? 1 : node.incomingValue / node.outgoingValue,
            node.color,
            node.outgoingValue < node.incomingValue ? 0 : (node.outgoingValue - node.incomingValue) / node.outgoingValue
        )

        for (const link of node.targetLinks!) {
            if (!visited.has(link)) {
                link.color = { ...weightedColor }
                nodeQueue.unshift(link.target)
                visited.add(link)
            }
        }
    }

    for (const link of graph.links) {
        if (!isValidColor(link.color)) {
            link.color = link.source.color
        }
    }
}
